import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Camera, Minus, Plus, X } from 'lucide-react';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
export const photoUrl = (id) => `${API_URL}/api/report-photos/${id}`;

export const Section = ({ title, hint, children }) => (
  <section className="glass rounded-[26px] p-5 sm:p-6 flex flex-col gap-4">
    <div>
      <h2 className="text-lg font-extrabold tracking-tight">{title}</h2>
      {hint && <p className="text-xs text-[var(--text-muted)] mt-1">{hint}</p>}
    </div>
    {children}
  </section>
);

export const FieldLabel = ({ children }) => <p className="label-text text-[var(--text-muted)] text-[0.6rem] mb-2">{children}</p>;

const field = 'bg-[var(--glass-soft)] border-[var(--glass-soft-border)] rounded-2xl h-11 text-center text-base font-bold';

export const NumberField = ({ label, unit, value, onChange, decimal = true }) => (
  <label className="flex flex-col">
    <FieldLabel>{label}{unit ? ` (${unit})` : ''}</FieldLabel>
    <Input inputMode={decimal ? 'decimal' : 'numeric'} value={value ?? ''} onChange={(e) => onChange(e.target.value)} className={field} />
  </label>
);

// 1-10 scale as tappable buttons (fits a 375px phone).
export const Scale = ({ label, value, onChange, low, high }) => (
  <div>
    <FieldLabel>{label}</FieldLabel>
    <div className="grid grid-cols-10 gap-1">
      {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
        <button key={n} type="button" onClick={() => onChange(value === n ? null : n)} aria-pressed={value === n}
          className={`h-10 rounded-xl text-sm font-bold ${value === n ? 'bg-[var(--cta-bg)] text-[var(--cta-fg)]' : 'glass-soft'}`}>{n}</button>
      ))}
    </div>
    {(low || high) && <div className="flex justify-between text-[10px] text-[var(--text-muted)] mt-1"><span>{low}</span><span>{high}</span></div>}
  </div>
);

export const Choice = ({ label, value, onChange, options }) => (
  <div>
    <FieldLabel>{label}</FieldLabel>
    <div className="grid grid-cols-3 gap-2">
      {options.map(([v, l]) => (
        <button key={v} type="button" onClick={() => onChange(value === v ? null : v)} aria-pressed={value === v}
          className={`h-10 rounded-full text-xs font-semibold ${value === v ? 'bg-[var(--cta-bg)] text-[var(--cta-fg)]' : 'glass-soft'}`}>{l}</button>
      ))}
    </div>
  </div>
);

export const Stepper = ({ label, value, onChange, step = 0.5, min = 0, max = 16, unit }) => (
  <div>
    <FieldLabel>{label}</FieldLabel>
    <div className="flex items-center gap-3">
      <button type="button" onClick={() => onChange(Math.max(min, (value ?? 7) - step))} aria-label="Mniej" className="glass-soft w-11 h-11 rounded-full flex items-center justify-center"><Minus className="w-4 h-4" /></button>
      <span className="big-num text-2xl flex-1 text-center">{value != null ? `${String(value).replace('.', ',')} ${unit}` : '—'}</span>
      <button type="button" onClick={() => onChange(Math.min(max, (value ?? 7) + step))} aria-label="Więcej" className="glass-soft w-11 h-11 rounded-full flex items-center justify-center"><Plus className="w-4 h-4" /></button>
    </div>
  </div>
);

export const TextField = ({ label, value, onChange, placeholder }) => (
  <div>
    <FieldLabel>{label}</FieldLabel>
    <Textarea value={value ?? ''} onChange={(e) => onChange(e.target.value)} rows={3} placeholder={placeholder}
      className="bg-[var(--glass-soft)] border-[var(--glass-soft-border)] rounded-2xl text-sm" />
  </div>
);

// Shrink to max 1600px JPEG before upload: phone photos are often 5-10 MB.
const downscale = (file) => new Promise((resolve) => {
  const img = new Image();
  img.onload = () => {
    const scale = Math.min(1, 1600 / Math.max(img.width, img.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(img.width * scale);
    canvas.height = Math.round(img.height * scale);
    canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
    canvas.toBlob((b) => resolve(b || file), 'image/jpeg', 0.85);
    URL.revokeObjectURL(img.src);
  };
  img.onerror = () => resolve(file);
  img.src = URL.createObjectURL(file);
});

export const PhotoSlot = ({ label, value, onChange }) => {
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const blob = await downscale(file);
      const form = new FormData();
      form.append('file', blob, 'photo.jpg');
      const res = await axios.post(`${API_URL}/api/client/report-photos`, form, { withCredentials: true });
      onChange(res.data.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Nie udało się wysłać zdjęcia');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex flex-col gap-1.5">
      <div className="relative aspect-[3/4] rounded-2xl overflow-hidden glass-soft flex items-center justify-center">
        {value ? (
          <>
            <img src={photoUrl(value)} alt={label} className="absolute inset-0 w-full h-full object-cover" />
            <button type="button" onClick={() => onChange(null)} aria-label={`Usuń zdjęcie ${label}`}
              className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full bg-black/70 text-white flex items-center justify-center"><X className="w-4 h-4" /></button>
          </>
        ) : (
          <label className="absolute inset-0 flex flex-col items-center justify-center gap-1 cursor-pointer text-[var(--text-muted)]">
            <Camera className="w-6 h-6" />
            <span className="text-[11px]">{busy ? 'Wysyłanie…' : 'Dodaj'}</span>
            <input type="file" accept="image/*" className="hidden" disabled={busy} onChange={(e) => upload(e.target.files?.[0])} />
          </label>
        )}
      </div>
      <span className="text-[11px] text-center text-[var(--text-muted)]">{label}</span>
    </div>
  );
};
